/**
 * The directory (WALLIYO-0131): the tab everybody gets.
 *
 * Read-only on purpose. It answers "who do I ask about this" with org
 * structure — name, role, teams, and whoever leads them — and nothing about
 * what anybody holds. The endpoint answers the same way, so this is not a
 * screen keeping a rule on its own.
 */

import type { DirectoryPerson, DirectoryPersonRef } from "@wallyo/contracts/console/hr";
import { t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { initials } from "../../shell/staff-context";
import { HR_DIRECTORY_LINKS, type DirectoryLinks } from "./hr-route";

export type DirectoryState =
  | { status: "loading"; people: readonly DirectoryPerson[] | null; total: number }
  | { status: "ready"; people: readonly DirectoryPerson[]; total: number }
  | { status: "failed"; people: readonly DirectoryPerson[] | null; total: number; error: unknown };

export type PersonState =
  | { status: "loading"; data: null }
  | { status: "ready"; data: { person: DirectoryPerson; teammates: readonly DirectoryPersonRef[] } }
  | { status: "failed"; data: null; error: unknown };

/** Their teams, each saying whether they lead it. */
function Teams({ person }: { person: DirectoryPerson }) {
  if (person.teams.length === 0) return <span className="foot">{t("directory.noTeam")}</span>;
  return (
    <span className="chips">
      {person.teams.map((team) => (
        <span className="chip" key={team.staffTeamId}>
          {team.name}
          {team.leads ? <span className="foot"> · {t("directory.leads")}</span> : null}
        </span>
      ))}
    </span>
  );
}

/**
 * Whoever leads their teams, each named with the team it came from. Somebody
 * on two teams has two managers, which is the honest answer when they do two
 * different jobs.
 */
function Managers({ person, links }: { person: DirectoryPerson; links: DirectoryLinks }) {
  if (person.managers.length === 0) return <span className="foot">{t("directory.noManager")}</span>;
  return (
    <span className="hr-stack">
      {person.managers.map((manager) => (
        <a key={`${manager.staffMemberId}:${manager.teamName}`} href={links.person(manager.uuid)}>
          {t("directory.managerOn", { name: manager.name, team: manager.teamName })}
        </a>
      ))}
    </span>
  );
}

export function DirectoryList(props: {
  links?: DirectoryLinks;
  state: DirectoryState;
  query: string;
  onQuery: (query: string) => void;
  onRetry: () => void;
}) {
  const { state, query } = props;
  const links = props.links ?? HR_DIRECTORY_LINKS;
  const people = state.people;

  return (
    <>
      <form
        className="hr-search"
        onSubmit={(event) => {
          event.preventDefault();
          props.onQuery(String(new FormData(event.currentTarget).get("q") ?? ""));
        }}
      >
        <label className="field">
          <span className="label">{t("directory.search")}</span>
          <input name="q" type="search" defaultValue={query} placeholder={t("directory.searchPlaceholder")} />
          <span className="foot">{t("directory.searchHelp")}</span>
        </label>
        <button className="btn" type="submit">
          {t("directory.go")}
        </button>
        {query !== "" ? (
          <button className="btn quiet" type="button" onClick={() => props.onQuery("")}>
            {t("directory.clear")}
          </button>
        ) : null}
      </form>

      <p className="foot">{t("directory.viewOnly")}</p>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("directory.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("directory.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!people && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("directory.loading")}</span>
        </div>
      ) : null}

      {people && people.length === 0 ? (
        <div className="empty">{query === "" ? t("directory.empty") : t("directory.none", { query })}</div>
      ) : null}

      {people && people.length > 0 ? (
        <>
          <p className="foot">
            {t("directory.count", { shown: String(people.length), total: String(state.total) })}
            {state.total > people.length ? ` · ${t("directory.more")}` : ""}
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("directory.colName")}</th>
                  <th>{t("directory.colRoles")}</th>
                  <th>{t("directory.colTeams")}</th>
                  <th>{t("directory.colManager")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <tr key={person.staffMemberId}>
                    <td>
                      <a href={links.person(person.uuid)}>{person.name}</a>
                      <div className="mono foot">{person.uuid}</div>
                      {person.status === "invited" ? <span className="pill">{t("directory.invited")}</span> : null}
                    </td>
                    <td>
                      {person.roles.length > 0 ? (
                        person.roles.join(", ")
                      ) : (
                        <span className="foot">{t("directory.noRole")}</span>
                      )}
                    </td>
                    <td>
                      <Teams person={person} />
                    </td>
                    <td>
                      <Managers person={person} links={links} />
                    </td>
                    <td className="num">
                      <a className="btn quiet small" href={links.person(person.uuid)}>
                        {t("directory.open")}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}

/** One person: who they are, their teams, and everybody else on them. */
export function DirectoryPersonView({
  state,
  onRetry,
  links = HR_DIRECTORY_LINKS,
}: {
  state: PersonState;
  onRetry: () => void;
  links?: DirectoryLinks;
}) {
  if (state.status === "loading") {
    return (
      <div className="empty" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>{t("directory.loading")}</span>
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div className="audit-error">
        <p className="foot">{t("directory.personFailed")}</p>
        <ErrorNotice error={state.error} />
        <div className="btns">
          <button className="btn" type="button" onClick={onRetry}>
            {t("directory.retry")}
          </button>
          <a className="btn quiet" href={links.list()}>
            {t("directory.back")}
          </a>
        </div>
      </div>
    );
  }

  const { person, teammates } = state.data;
  // Everybody on their teams, grouped by the team the answer came from: a flat
  // list of names would not say which half of somebody's job each one shares.
  const byTeam = new Map<string, DirectoryPersonRef[]>();
  for (const mate of teammates) {
    const list = byTeam.get(mate.teamName);
    if (list) list.push(mate);
    else byTeam.set(mate.teamName, [mate]);
  }

  return (
    <>
      <p>
        <a href={links.list()}>← {t("directory.back")}</a>
      </p>

      {/* The badge is the person, and it is the same object as your own
          profile's: name, work address, UUID. Only the page beside it
          differs — theirs answers "who do I ask about this" (WALLIYO-0137). */}
      <div className="person">
        <aside className="card person-badge">
          <span className="profile-avatar" aria-hidden="true">
            {initials(person.name)}
          </span>
          <h2>{person.name}</h2>
          <p className="profile-id">
            <span>{t("profile.emailLabel")}</span> {person.email}
          </p>
          <p className="profile-id">
            <span>{t("profile.uuidLabel")}</span> <code>{person.uuid}</code>
          </p>

          <span className="person-rule" aria-hidden="true" />

          <div className="person-facts">
            <span className="profile-caption">{t("directory.colRoles")}</span>
            <p className="profile-chips">
              {person.roles.length > 0 ? (
                person.roles.map((role) => (
                  <span className="chip" key={role}>
                    {role}
                  </span>
                ))
              ) : (
                <span className="foot">{t("directory.noRole")}</span>
              )}
            </p>
          </div>

          <span className="person-gap" />
          {/* Their page, so the button is how you reach them. */}
          <a className="btn quiet block" href={`mailto:${person.email}`}>
            {t("directory.emailThem")}
          </a>
        </aside>

        <div className="person-main">
          <section className="card">
            <h2>{t("directory.whereTheySit")}</h2>
            <dl className="hr-pairs">
              <dt>{t("directory.colTeams")}</dt>
              <dd>
                <Teams person={person} />
              </dd>
              <dt>{t("directory.colManager")}</dt>
              <dd>
                <Managers person={person} links={links} />
              </dd>
            </dl>
            <p className="foot">{t("directory.viewOnly")}</p>
          </section>

          <section className="card">
            <h2>{t("directory.teammates")}</h2>
            {teammates.length === 0 ? (
              <p className="foot">{t("directory.teammatesNone")}</p>
            ) : (
              [...byTeam.entries()].map(([teamName, mates]) => (
                <div key={teamName}>
                  <h3>{teamName}</h3>
                  <ul className="hr-plain">
                    {mates.map((mate) => (
                      <li key={mate.staffMemberId}>
                        <a href={links.person(mate.uuid)}>{mate.name}</a> <span className="mono foot">{mate.uuid}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>
        </div>
      </div>
    </>
  );
}
